import { FormControlLabel, Radio, RadioGroup } from '@material-ui/core';
import React, { useEffect, useReducer, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  FLOW_MAPPING,
  REDIRECT_STATUS_ACTIVE,
  REDIRECT_STATUS_INACTIVE,
  REDIRECT_TYPE_EXTERNAL,
  REDIRECT_TYPE_FLOW,
  REDIRECT_TYPE_IN_APP_PAGE,
} from '../../../constants/UiBuilderConst';
import {
  allUIBuilderFlowsSelector,
  allUIBuilderPagesSelector,
} from '../../../layouts/property/propertySlice';
import CustomSelect from '../../form-controls/custom-select/Custom-Select';
import CustomTextField from '../../form-controls/custom-text-field/Custom-Text-Field';
import ModuleIdSelector from '../module-id-selector/Module-Id-Selector';
import './Redirect-Selector.scss';

const RedirectSelector = (props) => {
  const {
    onValueChange,
    redirectType,
    value,
    onRedirectChange,
    isActive,
    error,
    areButtonsDisabled,
    isNameDisabled,
    showFlowSubSelection,
    flowSubSelection,
  } = props;

  const [type, setType] = useState(redirectType ? redirectType : REDIRECT_TYPE_IN_APP_PAGE);
  const [statusValue, setStatusValue] = useState(
    isActive ? REDIRECT_STATUS_ACTIVE : REDIRECT_STATUS_INACTIVE,
  );
  const originalPages = useSelector(allUIBuilderPagesSelector);
  const flows = useSelector(allUIBuilderFlowsSelector);
  const [formData, setFormData] = useReducer((state, newState) => ({ ...state, ...newState }), {
    inAppPage: {
      value: '',
      fieldName: 'Page',
      errors: {},
      validations: [],
      tab: 0,
    },
    external: {
      value: '',
      fieldName: 'External Link',
      errors: {},
      validations: [],
      tab: 0,
    },
    flow: {
      value: '',
      fieldName: 'Flow',
      errors: {},
      validations: [],
      tab: 0,
    },
    flowSubSelection: {
      value: '',
      fieldName: 'Flow Sub Module',
      errors: {},
      validations: [],
      tab: 0,
    },
  });

  useEffect(() => {
    setStatusValue(isActive ? REDIRECT_STATUS_ACTIVE : REDIRECT_STATUS_INACTIVE);
    const currFields = { ...formData };
    if (isActive) {
      switch (redirectType) {
        case REDIRECT_TYPE_IN_APP_PAGE:
          formData.inAppPage.validations = ['required'];
          break;
        case REDIRECT_TYPE_EXTERNAL:
          formData.external.validations = ['required', 'validUrl', 'safeURL', 'safeDomain'];
          break;
        case REDIRECT_TYPE_FLOW:
          formData.flow.validations = ['required'];
          break;
        default:
          formData.inAppPage.validations = ['required'];
          break;
      }
    }
    setFormData(currFields);
  }, [isActive]);

  useEffect(() => {
    setType(redirectType ? redirectType : REDIRECT_TYPE_IN_APP_PAGE);
  }, [redirectType]);

  useEffect(() => {
    const currFields = { ...formData };

    if (redirectType === REDIRECT_TYPE_IN_APP_PAGE) {
      currFields.inAppPage.value = value || '';
      currFields.external.value = '';
      currFields.flow.value = '';
      currFields.flowSubSelection.value = '';
    } else if (redirectType === REDIRECT_TYPE_EXTERNAL) {
      currFields.inAppPage.value = '';
      currFields.external.value = value || '';
      currFields.flow.value = '';
      currFields.flowSubSelection.value = '';
    } else if (redirectType === REDIRECT_TYPE_FLOW) {
      currFields.inAppPage.value = '';
      currFields.external.value = '';
      currFields.flow.value = value || '';
      currFields.flowSubSelection.value = flowSubSelection || '';
    } else {
      currFields.inAppPage.value = '';
      currFields.external.value = '';
      currFields.flow.value = '';
      currFields.flowSubSelection.value = '';
    }

    setFormData(currFields);
  }, [redirectType, value, flowSubSelection]);

  useEffect(() => {
    const currFields = { ...formData };
    if (error) {
      switch (type) {
        case REDIRECT_TYPE_IN_APP_PAGE:
          currFields.inAppPage.errors = error
            ? { required: `${currFields.inAppPage.fieldName} is required` }
            : {};
          currFields.external.errors = {};
          currFields.flow.errors = {};
          break;
        case REDIRECT_TYPE_EXTERNAL:
          currFields.inAppPage.errors = {};
          currFields.external.errors =
            value?.length < 1
              ? { required: `${currFields.external.fieldName} is required` }
              : { validUrl: `${currFields.external.fieldName} is Invalid` };
          currFields.flow.errors = {};
          break;
        case REDIRECT_TYPE_FLOW:
          if (currFields.flow.value) {
            currFields.inAppPage.errors = {};
            currFields.external.errors = {};
            currFields.flow.errors = {};
            currFields.flowSubSelection.errors =
              FLOW_MAPPING.hasOwnProperty(currFields.flow.value) &&
              !currFields.flowSubSelection.value
                ? {
                    required: `${currFields.flowSubSelection.fieldName} is required`,
                  }
                : {};
          } else {
            currFields.inAppPage.errors = {};
            currFields.external.errors = {};
            currFields.flow.errors = error
              ? { required: `${currFields.flow.fieldName} is required` }
              : {};
          }
          break;
        default:
          break;
      }
    } else {
      currFields.inAppPage.errors = {};
      currFields.external.errors = {};
      currFields.flow.errors = {};
    }
    setFormData(currFields);
  }, [error, type]);

  useEffect(() => {
    const value =
      type === REDIRECT_TYPE_IN_APP_PAGE
        ? formData.inAppPage.value
        : type === REDIRECT_TYPE_EXTERNAL
        ? formData.external.value
        : formData.flow.value;
    const updateValue = showFlowSubSelection
      ? { type: type, value: value, flowSubSelection: flowSubSelection || '' }
      : { type: type, value: value };

    onValueChange(updateValue);
  }, [formData.inAppPage.value, formData.external.value, formData.flow.value, type]);

  const onTypeChange = (e) => {
    if (e.target.value === REDIRECT_TYPE_IN_APP_PAGE) {
      formData.inAppPage.validations = ['required'];
      formData.external.validations = [];
      formData.flow.validations = [];
    } else if (e.target.value === REDIRECT_TYPE_EXTERNAL) {
      formData.inAppPage.validations = [];
      formData.external.validations = ['required', 'validUrl', 'safeURL', 'safeDomain'];
      formData.flow.validations = [];
    } else if (e.target.value === REDIRECT_TYPE_FLOW) {
      formData.inAppPage.validations = [];
      formData.external.validations = [];
      formData.flow.validations = ['required'];
    }
    setType(e.target.value);
  };

  const onStatusChange = (e) => {
    if (e.target.value === REDIRECT_STATUS_INACTIVE) {
      formData.inAppPage.validations = [];
      formData.external.validations = [];
      formData.flow.validations = [];
    } else {
      if (type === REDIRECT_TYPE_IN_APP_PAGE) {
        formData.inAppPage.validations = ['required'];
        formData.external.validations = [];
        formData.flow.validations = [];
      } else if (type === REDIRECT_TYPE_EXTERNAL) {
        formData.inAppPage.validations = [];
        formData.external.validations = ['required', 'validUrl', 'safeURL', 'safeDomain'];
        formData.flow.validations = [];
      } else if (type === REDIRECT_TYPE_FLOW) {
        formData.inAppPage.validations = [];
        formData.external.validations = [''];
        formData.flow.validations = ['required'];
      }
    }
    setStatusValue(e.target.value);
  };

  useEffect(() => {
    onRedirectChange(statusValue === REDIRECT_STATUS_ACTIVE);
    if (statusValue === REDIRECT_STATUS_INACTIVE) {
      const updateValue = showFlowSubSelection
        ? { type: type, value: '', flowSubSelection: flowSubSelection || '' }
        : { type: type, value: '' };
      onValueChange(updateValue);
    }
  }, [statusValue]);

  const handleFlowSubSelection = (subSel) => {
    const currField = { ...formData.flowSubSelection };

    if (showFlowSubSelection) {
      currField.value = subSel;
      if (subSel) {
        currField.errors = {};
        const value =
          type === REDIRECT_TYPE_IN_APP_PAGE
            ? formData.inAppPage.value
            : type === REDIRECT_TYPE_EXTERNAL
            ? formData.external.value
            : formData.flow.value;
        onValueChange({ type: type, value: value, flowSubSelection: subSel });
      } else {
        currField.errors = {
          required: `${currField.fieldName} is required`,
        };
      }
      setFormData({ flowSubSelection: currField });
    }
  };

  return (
    <div className='col-md-12 redirect-selector mb-3'>
      <div className='row align-items-center mb-4 mt-3'>
        {!isNameDisabled && <div className='col-md-4 title'>Redirect Link</div>}
        {!areButtonsDisabled && (
          <div className='col-md-8'>
            <RadioGroup
              row
              aria-label='statusValue'
              onChange={onStatusChange}
              value={statusValue}
              className='document-type'
              name='row-radio-buttons-group'
            >
              <FormControlLabel
                value={REDIRECT_STATUS_ACTIVE}
                control={<Radio color='primary' />}
                label='Active'
                className='me-5'
              />
              <FormControlLabel
                value={REDIRECT_STATUS_INACTIVE}
                control={<Radio color='primary' />}
                label='Inactive'
              />
            </RadioGroup>
          </div>
        )}
      </div>
      {statusValue === REDIRECT_STATUS_ACTIVE && (
        <>
          <div className='row'>
            <div className='col-md-12'>
              <RadioGroup
                row
                aria-label='documenttype'
                onChange={onTypeChange}
                value={type}
                className='document-type mb-3'
                name='row-radio-buttons-group'
              >
                <FormControlLabel
                  value={REDIRECT_TYPE_IN_APP_PAGE}
                  control={<Radio color='primary' />}
                  label='In-App Page'
                  className='me-5'
                />
                <FormControlLabel
                  value={REDIRECT_TYPE_EXTERNAL}
                  control={<Radio color='primary' />}
                  label='External'
                  className='me-5'
                />
                <FormControlLabel
                  value={REDIRECT_TYPE_FLOW}
                  control={<Radio color='primary' />}
                  label='Flow'
                />
              </RadioGroup>
            </div>
          </div>
          <div className='row'>
            {type === REDIRECT_TYPE_IN_APP_PAGE && (
              <div className='col-md-9'>
                <CustomSelect
                  item={formData.inAppPage}
                  handleInputChange={(e) => {
                    setFormData({ inAppPage: e });
                  }}
                  labelPrefix='Select'
                  options={originalPages}
                  hideNullOption={originalPages.length}
                />
              </div>
            )}
            {type === REDIRECT_TYPE_EXTERNAL && (
              <div className='col-md-9'>
                <CustomTextField
                  item={formData.external}
                  handleInputChange={(e) => {
                    setFormData({ external: e });
                  }}
                  maxLength='2048'
                  multiline={true}
                />
              </div>
            )}
            {type === REDIRECT_TYPE_FLOW && (
              <>
                <div className='col-md-9'>
                  <CustomSelect
                    item={formData.flow}
                    handleInputChange={(e) => {
                      setFormData({ flow: e });
                    }}
                    labelPrefix='Select'
                    options={flows}
                    labelField='label'
                    hideNullOption={flows.length}
                  />
                  {showFlowSubSelection && (
                    <div className='mt-3'>
                      {formData.flow.value && FLOW_MAPPING.hasOwnProperty(formData.flow.value) && (
                        <ModuleIdSelector
                          hotelModule={FLOW_MAPPING[formData.flow.value]}
                          hotelModuleId={formData.flowSubSelection.value}
                          required={true}
                          onChangeHotelModuleId={(e, moduleName) =>
                            handleFlowSubSelection(e.target.value)
                          }
                          requiredError={formData.flowSubSelection.errors.required}
                          error={Boolean(formData.flowSubSelection.errors.required)}
                        />
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default RedirectSelector;
